import cx from 'classnames';
import { forwardRef, memo, useImperativeHandle, useMemo, useState } from 'react';
import { EntityTypes } from '@wg/wows-entities/const';
import WoWSEntity from '@wg/wows-entities/wrappers/react/WoWSEntity';

import settings from '~/settings';
import { areAllTasksClaimed, getClaimedTasks, lastTaskClaimedAt } from '~/helpers/clanTasks';
import { localizedDateTime } from '~/helpers/internalization';
import { t } from '~/helpers/localization';

import styles from './TaskRow.scss';
import TaskRowBase, { renderCheckedSpan } from './TaskRowBase';

import type { ClanTask } from '~/Actions/ActionClanTasks';

export type TaskRowAchievementHandle = {
  animate: () => void;
};

type TaskRowAchievementProps = {
  tasks?: ClanTask[];
};

const TaskRowAchievement = forwardRef<TaskRowAchievementHandle, TaskRowAchievementProps>(({ tasks = [] }, ref) => {
  const [animationTick, setAnimationTick] = useState(0);

  useImperativeHandle(
    ref,
    () => ({
      animate: () => setAnimationTick((v) => v + 1),
    }),
    [],
  );

  const achievementGranted = useMemo(() => areAllTasksClaimed(tasks), [tasks]);
  const claimedTasks = useMemo(() => getClaimedTasks(tasks), [tasks]);

  const lastTaskClaimedAtLocalized = useMemo(() => {
    if (!achievementGranted) {
      return '';
    }
    const ts = lastTaskClaimedAt(tasks);
    return ts ? localizedDateTime(new Date(ts)) : '';
  }, [achievementGranted, tasks]);

  const achievementDescription = useMemo(() => t('Выполните все задачи клана и соберите награды'), []);

  const achievementIconTooltip = useMemo(() => {
    return achievementGranted
      ? renderCheckedSpan(t('Все задачи клана выполнены и награды собраны'))
      : achievementDescription;
  }, [achievementGranted, achievementDescription]);

  const progress = useMemo(() => {
    if (!tasks.length) {
      return 0;
    }
    return Math.min(claimedTasks.length / tasks.length, 1);
  }, [claimedTasks.length, tasks.length]);

  const { grandFleetAchievementCd, grandFleetAchievementSource } = useMemo(() => {
    const mediaPath = settings.mediaPath;
    const achievementsData = settings.achievements.data;
    const grandFleetAchievementCd = settings.darwin.grandFleetAchievementCd;

    const grandFleetAchievementSource =
      mediaPath + (achievementsData?.[grandFleetAchievementCd]?.icons.normal ?? 'unknown');

    return { grandFleetAchievementCd, grandFleetAchievementSource };
  }, []);

  return (
    <TaskRowBase
      title={t('Выполните все задачи клана')}
      description={achievementDescription}
      binary={false}
      progress={progress}
      total={tasks.length}
      isFinished={achievementGranted}
      isClaimed={achievementGranted}
      taskIconTooltip={achievementIconTooltip}
    >
      <WoWSEntity
        type={EntityTypes.ACHIEVEMENT}
        id={grandFleetAchievementCd}
        presentation={{
          bindTooltip: true,
          withTooltip: true,
          hideReceiveText: true,
          hideSpendText: true,
        }}
        customisation={{
          rewardReceiptDate: achievementGranted && lastTaskClaimedAtLocalized ? lastTaskClaimedAtLocalized : '',
        }}
      >
        <div key={animationTick} className={styles.achievementResourceWrapper}>
          {achievementGranted ? <span className={cx(styles.checkIcon, styles.checkIconLarge)} /> : null}
          <div
            className={cx(styles.achievementIcon, {
              [styles.achievementActiveIcon]: achievementGranted,
            })}
            style={{ backgroundImage: `url('${grandFleetAchievementSource}')` }}
          />
        </div>
      </WoWSEntity>
    </TaskRowBase>
  );
});

TaskRowAchievement.displayName = 'TaskRowAchievement';

export default memo(TaskRowAchievement);
